import { useSelector } from "react-redux";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { LedgerBoardsApi, GetLedgerStandingsResponse } from "@devour/client";
import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";

/**
 * Standings for a ledger board (LedgerBoard) — the public read path over
 * the score ledger. Optional-auth: logged-out users get redacted rows,
 * logged-in users additionally get isMe / own-row userId / myRank.
 *
 * periodKey semantics (mirrors the endpoint):
 *   - undefined → the server resolves the CURRENT period from the board's clock
 *   - ""        → all-time, bounded by the board's start (only offered when showAllTime is set)
 *   - "<n>"     → an explicit closed period, browsable because periods are durable rows
 *
 * Live via `useJoinLedgerBoardRoom` — the ledger write path broadcasts
 * SCORE_BOARD_UPDATE — with 60s polling underneath it as the floor for a
 * client that missed a socket frame.
 */
export function useGetLedgerStandings(
    ledgerBoardId: string | undefined,
    opts?: { periodKey?: string; limit?: number; offset?: number },
) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const limit = opts?.limit ?? 50;
    const offset = opts?.offset ?? 0;

    return useQuery({
        queryKey: ["ledger-standings", ledgerBoardId, opts?.periodKey ?? "current", limit, offset, fullToken?.id ?? "unauthenticated"],
        queryFn: async (): Promise<GetLedgerStandingsResponse> => {
            return new LedgerBoardsApi(getConfig(fullToken)).getLedgerBoardsBoardIdStandings({
                boardId: ledgerBoardId!,
                ...opts?.periodKey !== undefined ? { periodKey: opts.periodKey } : {},
                limit: String(limit),
                offset: String(offset),
            });
        },
        enabled: !!ledgerBoardId,
        refetchInterval: 60_000,
        // Keep the previous board on screen while a period switch loads — no skeleton flash
        placeholderData: keepPreviousData,
    });
}
